import type { TypedComparison } from './types'
import type { ImplementedRubricSpec } from '@/lib/types'

export interface RunConfiguration {
  name: string; display_name?: string; program?: string; framework: 'osworld2' | 'gymanything'
  models?: string[]; packages?: Record<string, string>
  task_selection?: { task_id: string; program: string; revision: string }[]
  task_references?: Record<string, { program: string; commit: string; repository: string }>
  tasks: string[]; dataset_revision?: string; pin_dataset_revision?: boolean; model: string; harness: string
  nodes?: number; nodes_per_model?: number; parallel?: number; rollouts: number; max_steps: number
  seed: number; seed_strategy: 'increment' | 'fixed'; on_demand?: boolean
  availability_zone?: string | null
}
export interface RunRecord {
  run_id: string; name: string; status: string; display_status: string; active: boolean
  created_at: string; created_by_email: string; terminated_at?: string
  /** Absent on a run launched here; 'agent' when the authoring agent launched its own,
   *  'cli' for `experiment up` (read-only here: stop it with `experiment down`). */
  origin?: 'agent' | 'cli'
  failure_reason?: string; infra_failures?: number; configuration: RunConfiguration
  metrics?: { jobs?: number; completed_jobs?: number; stopped_jobs?: number; failed_jobs?: number; input_tokens?: number; output_tokens?: number; duration_seconds?: number; mean_verifier_score?: number | null }
  fleet?: Fleet
}

/** One worker node, from its latest `nodes/<instance>.json` heartbeat. */
export type NodeState = 'pending' | 'booting' | 'staging' | 'starting' | 'running' | 'idle' | 'draining' | 'unreachable' | 'stopped'
export interface NodeJob { task: string; job_id?: string; attempt_id?: string; seed: number; elapsed_s: number; latest_sequence: number }
export interface RunNode {
  instance_id: string; state: NodeState; activity: string; phase?: string | null
  last_seen?: string | null; age_seconds: number | null; live: boolean
  slots: number; busy: number; free: number
  completed: number; infra_total: number; infra_streak: number; rate_limited: number
  running: NodeJob[]
}
/** Fleet capacity as the job queue sees it; only live, polling nodes count. */
export interface Fleet {
  requested: number; launched: number; reporting: number; polling: number; preparing: number
  unreachable: number; stopped: number; slots: number; busy_slots: number; free_slots: number
  launch_state: string | null; completed_on_nodes: number; infra_on_nodes: number
}

/** One node's own record (GET /eng/runs/<run_id>/nodes/<instance_id>), read only
 *  while its panel is open: the log is too big for the board's polling. */
export interface NodeDetail {
  instance_id: string
  /** False when the node published no log, so an empty failure list proves nothing. */
  attributed: boolean
  failures: { job_id: string; task_id?: string; ordinal: number; at: string; attempt: Attempt }[]
  logs: { title: string; text: string }[]
}

/** A task's name and prompt from the pinned dataset catalog (GET /eng/runs/tasks). */
export interface TaskInfo { task_id: string; title: string; prompt?: string }

/** Sanitized diagnostic supplied by REMI; never contains raw provider payloads. */
export interface RunDiagnostic {
  schema_version: number
  diagnostic_id?: string
  code: string
  stage?: string
  origin?: string
  message: string
  exception_type?: string
  facts?: Record<string, string | number | boolean | null>
}

/** A failure REMI recorded after the attempt's primary one (evidence upload, cleanup).
 *  It belongs to the same attempt: the primary failure stays the cause and drives retry. */
export interface AdditionalFailure {
  code: string
  stage?: 'publication' | 'cleanup' | null
  message: string
  diagnostic_id?: string | null
  exception_type?: string | null
}

/** One attempt at a job, from `outcomes/<job_id>/<attempt_id>.json`. */
export interface Attempt {
  promoted_regrade?: string | null
  recorded_steps?: number
  history_status?: 'complete' | 'incomplete' | 'unknown'
  machine_id?: number | null
  slot?: number | null
  started_at?: string | null
  ended_at?: string | null
  attempt_id?: string
  classification: string
  error_class?: string | null
  failure_code?: string | null
  diagnostic?: RunDiagnostic | null
  /** Failures after the primary one; never counted as failures of their own. */
  additional_failures?: AdditionalFailure[] | null
  model_started?: boolean
  retry_status?: 'queued' | 'retried' | 'not_retrying' | null
  retry_reason?: string | null
  sample_outcome?: string | null
  retry_decision?: string | null
  node_action?: string | null
  detail?: string
  summary?: EpisodeSummary
  /** The platform's verdict for an authoring-agent rollout, when it has one. */
  platform_classification?: string
  /** The authoring agent ended this rollout on purpose; the runner's class is the shutdown it saw. */
  stopped_by_agent?: boolean
  /** Raw runner stdout/stderr shipped off the node, by file name. */
  runner_logs?: Record<string, string>
  /** REMI holds this attempt's output; read it as text from `.../attempts/<id>/output`. */
  has_output?: boolean
}
/** One file of an attempt's output, as text (REMI's `logs` op). */
export interface OutputFile { path: string; name: string; instance_id: string; text: string; total_bytes: number }
/** One criterion of the task's rubric, as the verifier scored it.
 *  `label` is joined on by the server from the package's published rubric map; a criterion
 *  the map does not declare still arrives, with its id and no sentence. */
export interface VerifierCriterion extends TypedComparison {
  id: string; label?: string; component?: string; evidence_domain?: string
  passed?: boolean; score?: number; weight?: number
  required_for_pass?: boolean; feedback?: string
}
/** The parts of the episode's summary.json the page reads. */
export interface EpisodeSummary {
  duration_seconds?: number; steps?: number; reward?: number | null
  termination_reason?: string; truncated?: boolean
  token_usage?: Record<string, number>
  verifier?: { status?: string; passed?: boolean; reward?: number | null; feedback?: string; criteria?: VerifierCriterion[] }
}
/** One file the task's exporter wrote at finalize, as the node published it. */
export interface EvidenceFile {
  attempt_id: string; path: string; size: number; at: string; url: string
}
/** GET /eng/runs/<run_id>/rollouts/<job_id>/evidence — the model's own exports.
 *  Screenshots and the recording are excluded by the server and only counted. */
export interface EvidenceManifest {
  job_id: string; task_id: string; attempts: string[]
  excluded_media: number; total_bytes: number; files: EvidenceFile[]
}
export type JobStage = 'queued' | 'running' | 'completed' | 'stopped' | 'failed'

/** The runner's class for an episode the authoring agent ended on purpose. */
export const AGENT_TERMINATED = 'agent_terminated'

/** Attempts that went wrong. A stopped episode is neither a success nor a failure, and an
 *  in-flight one (REMI reports those as `pending`) has not ended yet. */
export function failedAttempts(attempts: Attempt[]): Attempt[] {
  return attempts.filter(attempt => attempt.classification !== 'success' && attempt.classification !== AGENT_TERMINATED
    && attempt.classification !== 'pending')
}
/** One job: one atomic queue message, one rollout of one task. */
export interface Rollout {
  history_complete?: boolean | null
  history_status?: 'complete' | 'incomplete' | 'unknown'
  job_id: string; task_id: string; seed: number; replicate: number; attempts: Attempt[]
  /** The model this job runs; several models can share one experiment. */
  model?: string | null
  attempt_kind?: 'model' | 'expert' | 'reference'
  stage?: JobStage; video_url?: string; instance_id?: string | null
  /** For a claimed job: its own preparation, or the model loop. */
  phase?: 'preparing' | 'sampling' | null
  max_steps?: number | null; step?: number; queue_position?: number
  waiting?: { code: string; text: string }
  heartbeat?: { instance_id?: string; attempt_id?: string; elapsed_seconds?: number; latest_sequence?: number }
}

/** Grading loads saved work directly rather than running a model episode. */
export function gradingIdentity(kind?: string) {
  if (kind === 'expert') return { label: 'Expert execution grading', icon: 'usercheck' } as const
  if (kind === 'reference') return { label: 'Final file grading', icon: 'filecheck' } as const
  return null
}
export interface TaskResult {
  task_id: string; duration_seconds: number
  token_usage: { input: number; output: number; reasoning: number }
  failures: number; rollouts: Rollout[]
  /** The task package's published rubric map, when this platform authored the task. */
  rubric_map?: ImplementedRubricSpec | null
}
export interface RunDetailPayload { run: RunRecord; tasks: TaskResult[]; nodes: RunNode[]; fleet: Fleet }
export interface Estimate {
  per_model?: { model: string; model_cost_usd: number[] | null; ec2_cost_usd: number[] | null; total_cost_usd: number[] | null }[]
  jobs_per_model?: number; total_jobs?: number
  pricing_error?: string | null; pricing_stale?: boolean; pricing_fetched_at?: string | null
  estimate_id: string; confidence: 'high' | 'low'; basis: string; sample_count: number
  model_cost_usd: number[] | null; ec2_cost_usd: number[] | null; total_cost_usd: number[] | null
  model_cost_unknown: boolean; ec2_cost_unknown?: boolean; assumptions: string[]; tokens: { input: number[]; output: number[] }
}
export interface CanonicalEvent {

  sequence: number; event_at: string; status: string; rationale?: string; rejection?: string; failure?: string
  proposed_actions: Record<string, unknown>[]; executed_actions: Record<string, unknown>[]
  token_total: { input: number; output: number; reasoning: number }
  screenshot: { key: string; url: string; width: number; height: number; sha256: string }
}

/** Cross-run board telemetry (GET /eng/runs/telemetry). */
export interface TelemetryBucket {
  start: string; attempts: number | null; failed_attempts: number | null; failed_jobs: number | null
  tokens: number | null; model_cost_usd: number; ec2_cost_usd: number
  model_cost_complete: boolean; ec2_cost_complete: boolean
  usage_missing_attempts?: number; usage_partial_attempts?: number; price_missing_attempts?: number
}
export interface TelemetryWindow {
  bucket_seconds: number
  /** Oldest first; the last bucket is still filling. */
  series: TelemetryBucket[]
  attempts: number | null; failed_attempts: number | null; failed_jobs: number | null; tokens: number | null
  model_cost_usd: number; ec2_cost_usd: number
  /** False when usage or a model price is missing; the amount is a known subtotal. */
  model_cost_complete: boolean
  /** Optional for compatibility with older API responses and cached snapshots. */
  usage_missing_attempts?: number; usage_partial_attempts?: number; price_missing_attempts?: number
  ec2_cost_complete: boolean
  model_credits_usd?: number; model_byok_usd?: number
  ec2_coverage_seconds?: number; ec2_unpriced_machines?: number
  cost_sources?: {
    environment: string
    model: { source: string; status: string; updated_at?: string; cutover_at?: string }
    ec2: { source: string; status: string; updated_at?: string; estimated: boolean }
  }
}
export interface BoardTelemetry {
  computed_at: string; cached: boolean; stale?: boolean
  statistics?: { status: 'ok' | 'error'; updated_at: string | null; error?: string }
  /** The first run the board can see; "all time" starts here, not before. */
  since: string
  runs_scanned: number | null; active_runs: number | null
  nodes: { working: number; idle: number; starting: number; unreachable: number
           total: number; slots: number; busy_slots: number } | null
  windows: Record<string, TelemetryWindow>
  failure_free: {
    seconds: number; at_least: boolean
    last_failure: { at: string; run_id: string; run_name: string; job_id: string; error_class?: string | null } | null
  } | null
}

/**
 * A job's stage, from the records the API already sent.
 *
 * The API sends `stage` too, and for `running` it is authoritative — only the server
 * sees the node heartbeat. Everything else is derivable here from the attempts, and
 * deriving it keeps the board honest against an API older than the page: the verdict
 * moved from "the last attempt" to "any attempt" (attempt ids are random hex, so
 * `attempts[-1]` was arbitrary), and a deployment lagging that change would otherwise
 * park an abandoned job in Queued.
 */
export function jobStage(rollout: Rollout): JobStage {
  if (rollout.attempts.some(attempt => attempt.classification === 'success')) return 'completed'
  if (rollout.stage === 'running') return 'running'
  // The authoring agent ended it on purpose: done, graded by nobody, failed by nothing.
  if (rollout.attempts.some(attempt => attempt.classification === AGENT_TERMINATED)) return 'stopped'
  // Once an attempt records no_retry the node has deleted the message, and an invalid
  // definition is never rerun: either verdict is terminal, whichever attempt carries it.
  if (rollout.attempts.some(attempt =>
    attempt.retry_decision === 'no_retry' || attempt.sample_outcome === 'invalid')) return 'failed'
  return rollout.stage ?? 'queued'
}

/** Program labels are derived from jobs' task references, not an experiment setting. */
export function runPrograms(configuration: RunConfiguration): string {
  return [...new Set(Object.values(configuration.task_references ?? {}).map(ref => ref.program)
    .concat(configuration.program ? [configuration.program] : []))].join(', ')
}
